// ============================================================================
// saveConsultantContent.js
// Saves AI-generated HTML for a consultant tab back to the ProcessModel table.
//
// Usage (Forge):
//   await saveConsultantContent({ modelId, column: 'ai_process_health_assessment', html, AppVersion: 'FORGE' });
//
// Usage:
//   await saveConsultantContent({ modelId, column: 'ai_process_health_assessment', html, AppVersion: 'SNOW' });
//
// Usage (SNOW):
//   await saveConsultantContent({ modelId, column: 'ai_process_health_assessment', html, AppVersion: 'SNOW' });
//
// Valid column values:
//   ai_process_health_assessment | ai_intelligence | ai_execution_strategy
//   ai_trend_analysis | ai_consulting_report | ai_framework_assessment | ai_quick_healthcheck
// ============================================================================

import { setCachedConsultantContent } from '@process-advisor/process-miner/consultant/consultantContentState';

let forgeModule = null;
const loadForgeModule = async () => {
  if (!forgeModule) forgeModule = await import('@forge/bridge');
  return forgeModule;
};


let _snApi = null;
export function setSNApiForConsultant(snApi) { _snApi = snApi; }

const FORGE_SAFE_INVOKE_PAYLOAD_BYTES = 170 * 1024;
const FORGE_HTML_CHUNK_CHARS = 60000;

function estimatePayloadBytes(value) {
  const str = typeof value === 'string' ? value : JSON.stringify(value || {});
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(str).length;
  }
  // Browser fallback. Good enough for the mostly-ASCII HTML generated here.
  return unescape(encodeURIComponent(str)).length;
}

function makeChunkSessionId(modelId, column) {
  const rand = Math.random().toString(36).slice(2);
  return `${modelId || 'model'}-${column || 'column'}-${Date.now()}-${rand}`;
}

async function saveForgeConsultantContentChunked({ invoke, modelId, column, html }) {
  const sessionId = makeChunkSessionId(modelId, column);
  const totalChunks = Math.ceil(html.length / FORGE_HTML_CHUNK_CHARS);

  for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex += 1) {
    const chunk = html.slice(
      chunkIndex * FORGE_HTML_CHUNK_CHARS,
      (chunkIndex + 1) * FORGE_HTML_CHUNK_CHARS
    );

    const response = await invoke('saveConsultantContentChunk', {
      modelId,
      column,
      sessionId,
      chunkIndex,
      totalChunks,
      chunk
    });

    if (response && response.success === false) {
      throw new Error(response.error || `chunked save failed at chunk ${chunkIndex + 1}/${totalChunks}`);
    }
  }

  return { success: true, chunked: true, totalChunks };
}

/**
 * @param {object} opts
 * @param {string} opts.modelId       - Process model UUID
 * @param {string} opts.column        - One of the ai_* column names
 * @param {string} opts.html          - Rendered HTML string to persist
 * @param {string} opts.AppVersion   - 'FORGE' | 'SNOW'
 */
export async function saveConsultantContent({ modelId, column, html, AppVersion }) {
  if (!modelId || !column || !html) {
    console.warn('saveConsultantContent: missing modelId, column, or html — skipping save');
    return;
  }

  try {
    if (AppVersion === 'FORGE') {
      const { invoke } = await loadForgeModule();
      const payload = { modelId, column, html };
      const payloadBytes = estimatePayloadBytes(payload);

      if (payloadBytes > FORGE_SAFE_INVOKE_PAYLOAD_BYTES) {
        const result = await saveForgeConsultantContentChunked({ invoke, modelId, column, html });
        setCachedConsultantContent({ modelId, column, html });
        console.log(
          `saveConsultantContent [FORGE]: saved ${column} for model ${modelId} using ${result.totalChunks} chunks (${payloadBytes} bytes)`
        );
      } else {
        await invoke('saveConsultantContent', payload);
        setCachedConsultantContent({ modelId, column, html });
        console.log(`saveConsultantContent [FORGE]: saved ${column} for model ${modelId}`);
      }

    } else if (AppVersion === 'SNOW') {
      // Optimistically cache first. In ServiceNow local/standalone mode the
      // PATCH can save successfully but the browser may still throw
      // TypeError: Failed to fetch when it cannot read the response.
      setCachedConsultantContent({ modelId, column, html });

      if (_snApi && typeof _snApi.saveConsultantContent === 'function') {
        await _snApi.saveConsultantContent(modelId, column, html);
        console.log(`saveConsultantContent [SNOW]: saved ${column} for model ${modelId}`);
      } else {
        // Defensive fallback for embedded builds that reach this module before
        // SNProcessMiner has injected snApi. The ServiceNow forge-bridge stub
        // maps this invoke back to snApi.saveConsultantContent.
        const { invoke } = await loadForgeModule();
        await invoke('saveConsultantContent', { modelId, column, html });
        console.log(`saveConsultantContent [SNOW/bridge]: saved ${column} for model ${modelId}`);
      }
    } else {
      console.warn(`saveConsultantContent: unknown AppVersion "${AppVersion}" — skipping save`);
    }
  } catch (err) {
    // ServiceNow can persist the update while local/standalone fetch still
    // throws because the browser cannot confirm the response. The content is
    // already cached, and the model reload path will confirm persistence.
    if (AppVersion === 'SNOW') {
      console.warn(
        `saveConsultantContent [SNOW]: ${column} save response could not be confirmed; content may still be saved successfully.`,
        err
      );
      return;
    }

    // Log but don't throw — a save failure should never block the UI.
    console.error(`saveConsultantContent: failed to save ${column}:`, err);
  }
}
